// tabs/RestituitTab.js
import React, { useState, useEffect, useMemo } from "react";
import axios from "axios";
import { jsPDF } from "jspdf";
import autoTable from "jspdf-autotable";
import * as XLSX from "xlsx";
import {
  Box,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  Typography,
  Paper,
  Checkbox,
  Stack,
  Grid,
  Card,
  CardContent,
  TextField,
  Chip,
  Backdrop,
  CircularProgress,
} from "@mui/material";
import CloudDownloadIcon from "@mui/icons-material/CloudDownload";
import RefreshIcon from "@mui/icons-material/Refresh";
import Inventory2Icon from "@mui/icons-material/Inventory2";
import PaidIcon from "@mui/icons-material/Paid";
import TrendingDownIcon from "@mui/icons-material/TrendingDown";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";

const DateRangeInput = React.forwardRef(function DateRangeInput(
  { value, onClick, placeholder },
  ref,
) {
  return (
    <TextField
      fullWidth
      variant="outlined"
      label="Interval de date"
      value={value}
      onClick={onClick}
      inputRef={ref}
      placeholder={placeholder}
      InputProps={{ readOnly: true }}
    />
  );
});

function RestituitTab() {
  const [startDate, setStartDate] = useState(null);
  const [endDate, setEndDate] = useState(null);
  const [restituitData, setRestituitData] = useState([]);
  const [filteredData, setFilteredData] = useState([]);
  const [isExportingZip, setIsExportingZip] = useState(false);
  const [isPreparingExport, setIsPreparingExport] = useState(false);
  const [preparedExport, setPreparedExport] = useState(null);
  const [selectedQuickRange, setSelectedQuickRange] = useState("");
  const [sortKey, setSortKey] = useState("dataRetur");
  const [sortDir, setSortDir] = useState("desc");
  const requestCounterRef = React.useRef(0);
  const generatedAt = useMemo(() => new Date().toLocaleDateString("ro-RO"), []);
  const filteredRangeLabel = useMemo(() => {
    if (!startDate || !endDate) {
      return "Toate datele";
    }
    const startLabel = startDate.toLocaleDateString("ro-RO");
    const endLabel = endDate.toLocaleDateString("ro-RO");
    return `${startLabel} - ${endLabel}`;
  }, [startDate, endDate]);

  const getRowDateValue = (row) => row?.dataRetur || row?.dataRestituit || "";

  const formatDisplayDate = (valueOrRow) => {
    const rawValue =
      typeof valueOrRow === "string" ? valueOrRow : getRowDateValue(valueOrRow);
    if (!rawValue) return "—";
    const match = rawValue.match(/(\d{1,2})[._-](\d{1,2})[._-](\d{4})/);
    if (!match) return rawValue;
    const day = match[1].padStart(2, "0");
    const month = match[2].padStart(2, "0");
    const year = match[3];
    return `${day}/${month}/${year}`;
  };

  const actionButtonStyles = {
    textTransform: "none",
    borderRadius: 3,
    px: 2.5,
  };

  const formatCurrency = useMemo(
    () =>
      new Intl.NumberFormat("ro-RO", {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      }),
    [],
  );

  const summaryTotals = useMemo(() => {
    const count = filteredData.length;
    const totals = filteredData.reduce(
      (acc, row) => {
        const intrare = Number(row.pretIntrareRON) || 0;
        const vanzare = Number(row.pretVanzareRON) || 0;
        acc.totalIntrare += intrare;
        acc.totalVanzare += vanzare;
        return acc;
      },
      { totalIntrare: 0, totalVanzare: 0 },
    );
    const profitPierdut = totals.totalIntrare - totals.totalVanzare;
    return { count, ...totals, profitPierdut };
  }, [filteredData]);

  const handleSort = (key) => {
    setSortDir((prevDir) =>
      sortKey === key ? (prevDir === "asc" ? "desc" : "asc") : "asc",
    );
    setSortKey(key);
  };

  const startOfDay = (date) => {
    const d = new Date(date);
    d.setHours(0, 0, 0, 0);
    return d;
  };

  const endOfDay = (date) => {
    const d = new Date(date);
    d.setHours(23, 59, 59, 999);
    return d;
  };

  const sortedData = useMemo(() => {
    if (!Array.isArray(filteredData)) return [];
    const multiplier = sortDir === "asc" ? 1 : -1;
    return [...filteredData].sort((a, b) => {
      if (sortKey === "sku") {
        const skuA = (a.sku || "").toString().toLowerCase();
        const skuB = (b.sku || "").toString().toLowerCase();
        return skuA.localeCompare(skuB) * multiplier;
      }
      if (sortKey === "dataRetur") {
        const valueA = (getRowDateValue(a) || "").toString();
        const valueB = (getRowDateValue(b) || "").toString();
        return valueA.localeCompare(valueB) * multiplier;
      }
      return 0;
    });
  }, [filteredData, sortDir, sortKey]);

  const quickRangeOptions = [
    { key: "today", label: "Astăzi" },
    { key: "last7", label: "Ultimele 7 zile" },
    { key: "last30", label: "Ultimele 30 de zile" },
    { key: "currentMonth", label: "Luna curentă" },
    { key: "previousMonth", label: "Luna anterioară" },
  ];

  const handleClearFilters = () => {
    setStartDate(null);
    setEndDate(null);
    setFilteredData(restituitData);
    setSelectedQuickRange("");
  };

  const formatMoney = (value) => {
    if (value === null || value === undefined || value === "") {
      return "—";
    }
    const numeric = Number(value);
    if (Number.isNaN(numeric)) {
      return "—";
    }
    return formatCurrency.format(numeric);
  };

  const formatDateForQuery = (date) => {
    if (!date) return "";
    const d = new Date(date);
    if (Number.isNaN(d.getTime())) return "";
    const year = d.getFullYear();
    const month = String(d.getMonth() + 1).padStart(2, "0");
    const day = String(d.getDate()).padStart(2, "0");
    return `${year}-${month}-${day}`;
  };

  const fetchRestituitData = async (range) => {
    try {
      const requestId = (requestCounterRef.current += 1);
      const params = range?.start && range?.end ? range : undefined;
      const response = await axios.get(
        "https://server-9p4hx.kinsta.app/api/jurnale/restituit",
        params ? { params } : undefined,
      );
      if (requestId !== requestCounterRef.current) return;
      const rows = Array.isArray(response.data) ? response.data : [];
      setRestituitData(rows);
      setFilteredData(rows);
    } catch (error) {
      console.error("Eroare la preluarea datelor Restituit:", error);
    }
  };

  useEffect(() => {
    if (startDate && endDate) {
      fetchRestituitData({
        start: formatDateForQuery(startDate),
        end: formatDateForQuery(endDate),
      });
      return;
    }
    if (!startDate && !endDate) {
      fetchRestituitData();
    }
  }, [startDate, endDate]);

  useEffect(() => {
    let cancelled = false;

    const prepareExportAssets = async () => {
      if (!filteredData.length) {
        setPreparedExport(null);
        return;
      }
      setIsPreparingExport(true);
      try {
        const rowsFormatted = filteredData.map((row) => ({
          REC: row.rec === 1 ? "✔️" : "",
          "Nr. Jurnal": row.nrJurnalSpecial || "—",
          "Tip Contract": row.tipContract || "—",
          SKU: row.sku || "—",
          "Nume și Prenume": row.numeSiPrenume || "—",
          Summary: row.summary || "—",
          "Preț Intrare":
            row.pretIntrareRON != null
              ? Number(row.pretIntrareRON).toFixed(2)
              : "—",
          "Preț Vânzare":
            row.pretVanzareRON != null
              ? Number(row.pretVanzareRON).toFixed(2)
              : "—",
          "Data Retur": formatDisplayDate(row),
        }));
        const wb = XLSX.utils.book_new();
        const ws = XLSX.utils.json_to_sheet(rowsFormatted);
        XLSX.utils.book_append_sheet(wb, ws, "Restituit");
        const excelBuffer = XLSX.write(wb, { bookType: "xlsx", type: "array" });
        const excelBlob = new Blob([excelBuffer], {
          type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
        });

        const doc = new jsPDF({ orientation: "landscape", format: "a4" });
        doc.setFontSize(14);
        doc.text("Jurnal Restituit", 14, 14);
        doc.setFontSize(9);
        doc.text(`Generat la: ${generatedAt}`, 14, 20);
        doc.text(`Filtru: ${filteredRangeLabel}`, 14, 24);
        const headers = [
          "REC",
          "Nr. Jurnal",
          "Tip Contract",
          "SKU",
          "Nume și Prenume",
          "Summary",
          "Preț Intrare",
          "Preț Vânzare",
          "Data Retur",
        ];
        const rows = filteredData.map((row) => [
          row.rec === 1 ? "✔️" : "",
          row.nrJurnalSpecial || "—",
          row.tipContract || "—",
          row.sku || "—",
          row.numeSiPrenume || "—",
          row.summary || "—",
          row.pretIntrareRON != null
            ? Number(row.pretIntrareRON).toFixed(2)
            : "—",
          row.pretVanzareRON != null
            ? Number(row.pretVanzareRON).toFixed(2)
            : "—",
          formatDisplayDate(row),
        ]);
        autoTable(doc, {
          head: [headers],
          body: rows,
          startY: 30,
          styles: { fontSize: 8 },
          headStyles: { fillColor: [0, 123, 255], textColor: 255 },
        });
        const pdfBlob = doc.output("blob");

        const blobToBase64 = (blob) =>
          new Promise((resolve, reject) => {
            const reader = new FileReader();
            reader.onloadend = () => resolve(reader.result.split(",")[1]);
            reader.onerror = reject;
            reader.readAsDataURL(blob);
          });

        const [pdfBase64, excelBase64] = await Promise.all([
          blobToBase64(pdfBlob),
          blobToBase64(excelBlob),
        ]);

        if (!cancelled) {
          setPreparedExport({
            pdfBlob,
            excelBlob,
            pdfBase64,
            excelBase64,
          });
        }
      } catch (error) {
        console.error("Eroare la pregatirea exporturilor Restituit:", error);
        if (!cancelled) setPreparedExport(null);
      } finally {
        if (!cancelled) setIsPreparingExport(false);
      }
    };

    prepareExportAssets();

    return () => {
      cancelled = true;
    };
  }, [filteredData, filteredRangeLabel, generatedAt]);

  const exportToExcel = (data) => {
    if (preparedExport?.excelBlob) {
      const url = window.URL.createObjectURL(preparedExport.excelBlob);
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", "Export_Restituit.xlsx");
      document.body.appendChild(link);
      link.click();
      link.parentNode.removeChild(link);
      window.URL.revokeObjectURL(url);
      return;
    }
    const rowsFormatted = data.map((row) => ({
      REC: row.rec === 1 ? "✔️" : "",
      "Nr. Jurnal": row.nrJurnalSpecial || "—",
      "Tip Contract": row.tipContract || "—",
      SKU: row.sku || "—",
      "Nume și Prenume": row.numeSiPrenume || "—",
      Summary: row.summary || "—",
      "Preț Intrare":
        row.pretIntrareRON != null
          ? Number(row.pretIntrareRON).toFixed(2)
          : "—",
      "Preț Vânzare":
        row.pretVanzareRON != null
          ? Number(row.pretVanzareRON).toFixed(2)
          : "—",
      "Data Retur": formatDisplayDate(row),
    }));

    const wb = XLSX.utils.book_new();
    const ws = XLSX.utils.json_to_sheet(rowsFormatted);
    XLSX.utils.book_append_sheet(wb, ws, "Restituit");
    XLSX.writeFile(wb, "Export_Restituit.xlsx");
  };

  const exportToPDF = (data) => {
    if (preparedExport?.pdfBlob) {
      const url = window.URL.createObjectURL(preparedExport.pdfBlob);
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", "Export_Restituit.pdf");
      document.body.appendChild(link);
      link.click();
      link.parentNode.removeChild(link);
      window.URL.revokeObjectURL(url);
      return;
    }
    const doc = new jsPDF({ orientation: "landscape", format: "a4" });
    const headers = [
      "REC",
      "Nr. Jurnal",
      "Tip Contract",
      "SKU",
      "Nume și Prenume",
      "Summary",
      "Preț Intrare",
      "Preț Vânzare",
      "Data Retur",
    ];
    const rows = data.map((row) => [
      row.rec === 1 ? "✔️" : "",
      row.nrJurnalSpecial || "—",
      row.tipContract || "—",
      row.sku || "—",
      row.numeSiPrenume || "—",
      row.summary || "—",
      row.pretIntrareRON != null ? Number(row.pretIntrareRON).toFixed(2) : "—",
      row.pretVanzareRON != null ? Number(row.pretVanzareRON).toFixed(2) : "—",
      formatDisplayDate(row),
    ]);

    doc.setFontSize(14);
    doc.text("Jurnal Restituit", 14, 14);
    doc.setFontSize(9);
    doc.text(`Generat la: ${generatedAt}`, 14, 20);
    doc.text(`Filtru: ${filteredRangeLabel}`, 14, 24);

    autoTable(doc, {
      head: [headers],
      body: rows,
      startY: 30,
      styles: { fontSize: 8 },
      headStyles: { fillColor: [0, 123, 255], textColor: 255 },
    });

    doc.save("Export_Restituit.pdf");
  };

  const handleExportZipRestituit = async () => {
    try {
      setIsExportingZip(true);
      const rawData = filteredData.map((row) => ({
        rec: row.rec,
        nrJurnalSpecial: row.nrJurnalSpecial,
        tipContract: row.tipContract,
        sku: row.sku,
        numeSiPrenume: row.numeSiPrenume,
        summary: row.summary,
        pretIntrareRON: row.pretIntrareRON,
        dataRetur: getRowDateValue(row),
        dataRestituit: getRowDateValue(row),
        issue_id: row.issue_id,
      }));

      // Convert blobs to base64
      const blobToBase64 = (blob) =>
        new Promise((resolve, reject) => {
          const reader = new FileReader();
          reader.onloadend = () => resolve(reader.result.split(",")[1]);
          reader.onerror = reject;
          reader.readAsDataURL(blob);
        });

      let pdfBase64 = preparedExport?.pdfBase64;
      let excelBase64 = preparedExport?.excelBase64;
      if (!pdfBase64 || !excelBase64) {
        const rowsFormatted = filteredData.map((row) => ({
          REC: row.rec === 1 ? "✔️" : "",
          "Nr. Jurnal": row.nrJurnalSpecial || "—",
          "Tip Contract": row.tipContract || "—",
          SKU: row.sku || "—",
          "Nume și Prenume": row.numeSiPrenume || "—",
          Summary: row.summary || "—",
          "Preț Intrare":
            row.pretIntrareRON != null
              ? Number(row.pretIntrareRON).toFixed(2)
              : "—",
          "Preț Vânzare":
            row.pretVanzareRON != null
              ? Number(row.pretVanzareRON).toFixed(2)
              : "—",
          "Data Retur": formatDisplayDate(row),
        }));
        const wb = XLSX.utils.book_new();
        const ws = XLSX.utils.json_to_sheet(rowsFormatted);
        XLSX.utils.book_append_sheet(wb, ws, "Restituit");
        const excelBuffer = XLSX.write(wb, { bookType: "xlsx", type: "array" });
        const excelBlob = new Blob([excelBuffer], {
          type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
        });

        const doc = new jsPDF({ orientation: "landscape", format: "a4" });
        doc.setFontSize(14);
        doc.text("Jurnal Restituit", 14, 14);
        doc.setFontSize(9);
        doc.text(`Generat la: ${generatedAt}`, 14, 20);
        doc.text(`Filtru: ${filteredRangeLabel}`, 14, 24);
        const headers = [
          "REC",
          "Nr. Jurnal",
          "Tip Contract",
          "SKU",
          "Nume și Prenume",
          "Summary",
          "Preț Intrare",
          "Preț Vânzare",
          "Data Retur",
        ];
        const rows = filteredData.map((row) => [
          row.rec === 1 ? "✔️" : "",
          row.nrJurnalSpecial || "—",
          row.tipContract || "—",
          row.sku || "—",
          row.numeSiPrenume || "—",
          row.summary || "—",
          row.pretIntrareRON != null
            ? Number(row.pretIntrareRON).toFixed(2)
            : "—",
          row.pretVanzareRON != null
            ? Number(row.pretVanzareRON).toFixed(2)
            : "—",
          formatDisplayDate(row),
        ]);
        autoTable(doc, {
          head: [headers],
          body: rows,
          startY: 30,
          styles: { fontSize: 8 },
          headStyles: { fillColor: [0, 123, 255], textColor: 255 },
        });
        const pdfBlob = doc.output("blob");

        pdfBase64 = await blobToBase64(pdfBlob);
        excelBase64 = await blobToBase64(excelBlob);
      }

      const payload = {
        tasks: filteredData,
        rawData,
        fileName: "Export_Restituit",
        pdfBuffer: pdfBase64,
        excelBuffer: excelBase64,
      };

      const response = await axios.post(
        "https://server-9p4hx.kinsta.app/api/tasks/export-zip-server-restituit",
        payload,
        {
          responseType: "blob",
        },
      );

      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", "Export_Restituit.zip");
      document.body.appendChild(link);
      link.click();
      link.parentNode.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error("Eroare la exportul Zip Restituit:", error);
    } finally {
      setIsExportingZip(false);
    }
  };

  const handleRefreshTasks = async () => {
    if (!startDate || !endDate) return;
    try {
      await axios.post(
        "https://server-9p4hx.kinsta.app/api/jurnale/refresh-tasks",
      );
    } catch (error) {
      console.error("Eroare la refresh cache Restituit:", error);
    }
    fetchRestituitData({
      start: formatDateForQuery(startDate),
      end: formatDateForQuery(endDate),
    });
  };
  const handleRangeChange = (dates) => {
    const [start, end] = dates;
    setSelectedQuickRange("");
    setStartDate(start);
    setEndDate(end);
  };

  const handleQuickRangeSelect = (rangeKey) => {
    const today = new Date();
    let range = null;

    switch (rangeKey) {
      case "today": {
        range = {
          start: startOfDay(today),
          end: endOfDay(today),
        };
        break;
      }
      case "last7": {
        const start = new Date(today);
        start.setDate(start.getDate() - 6);
        range = {
          start: startOfDay(start),
          end: endOfDay(today),
        };
        break;
      }
      case "last30": {
        const start = new Date(today);
        start.setDate(start.getDate() - 29);
        range = {
          start: startOfDay(start),
          end: endOfDay(today),
        };
        break;
      }
      case "currentMonth": {
        const start = new Date(today.getFullYear(), today.getMonth(), 1);
        const end = new Date(today.getFullYear(), today.getMonth() + 1, 0);
        range = {
          start: startOfDay(start),
          end: endOfDay(end),
        };
        break;
      }
      case "previousMonth": {
        const start = new Date(today.getFullYear(), today.getMonth() - 1, 1);
        const end = new Date(today.getFullYear(), today.getMonth(), 0);
        range = {
          start: startOfDay(start),
          end: endOfDay(end),
        };
        break;
      }
      default:
        break;
    }

    if (range) {
      setSelectedQuickRange(rangeKey);
      setStartDate(range.start);
      setEndDate(range.end);
    }
  };

  const handleRecChange = async (issue_id, newRecValue) => {
    try {
      const response = await axios.post(
        "https://server-9p4hx.kinsta.app/api/jurnale/update-rec",
        {
          issue_id,
          rec: newRecValue ? 1 : 0,
        },
      );

      if (response.status === 200) {
        const updated = filteredData.map((row) =>
          row.issue_id === issue_id
            ? { ...row, rec: newRecValue ? 1 : 0 }
            : row,
        );
        setFilteredData(updated);
      }
    } catch (error) {
      console.error("Eroare la actualizarea valorii REC:", error);
    }
  };

  return (
    <>
      <Paper
        sx={{
          p: 3,
          borderRadius: 4,
          mb: 3,
          background: "linear-gradient(135deg, #1d2671 0%, #c33764 100%)",
          color: "common.white",
          boxShadow: "0px 25px 60px rgba(23, 32, 90, 0.35)",
        }}
      >
        <Stack
          direction={{ xs: "column", lg: "row" }}
          spacing={3}
          justifyContent="space-between"
          alignItems={{ xs: "flex-start", lg: "center" }}
        >
          <Box>
            <Typography variant="h5" fontWeight={600} gutterBottom>
              Panou Restituiri
            </Typography>
            <Typography variant="body2" sx={{ opacity: 0.9 }}>
              Monitorizează produsele returnate, exportă rapoarte și urmărește
              impactul financiar.
            </Typography>
          </Box>
          <Stack direction={{ xs: "column", sm: "row" }} spacing={1}>
            <Button
              startIcon={<CloudDownloadIcon />}
              onClick={() => exportToExcel(filteredData)}
              disabled={isPreparingExport}
              sx={{
                ...actionButtonStyles,
                bgcolor: "rgba(255,255,255,0.22)",
                color: "#fff",
              }}
            >
              {isPreparingExport ? "Pregătim Excel" : "Export Excel"}
            </Button>
            <Button
              startIcon={<CloudDownloadIcon />}
              onClick={() => exportToPDF(filteredData)}
              disabled={isPreparingExport}
              sx={{
                ...actionButtonStyles,
                bgcolor: "rgba(255,255,255,0.22)",
                color: "#fff",
              }}
            >
              {isPreparingExport ? "Pregătim PDF" : "Export PDF"}
            </Button>
            <Button
              startIcon={<CloudDownloadIcon />}
              onClick={handleExportZipRestituit}
              disabled={isPreparingExport}
              sx={{
                ...actionButtonStyles,
                bgcolor: "rgba(0,0,0,0.25)",
                color: "#fff",
              }}
            >
              {isPreparingExport ? "Pregătim Zip" : "Export Zip"}
            </Button>
            <Button
              startIcon={<RefreshIcon />}
              onClick={handleRefreshTasks}
              sx={{
                ...actionButtonStyles,
                bgcolor: "rgba(0,0,0,0.35)",
                color: "#fff",
              }}
            >
              Reîncarcă datele
            </Button>
          </Stack>
        </Stack>
      </Paper>

      <Paper sx={{ p: 3, borderRadius: 4, mb: 3 }}>
        <Stack
          direction={{ xs: "column", md: "row" }}
          spacing={2}
          justifyContent="space-between"
          alignItems={{ xs: "flex-start", md: "center" }}
        >
          <Box sx={{ width: { xs: "100%", sm: 320 } }}>
            <Typography variant="body2" color="text.secondary" mb={0.5}>
              Interval Restituiri
            </Typography>
            <DatePicker
              selectsRange
              startDate={startDate}
              endDate={endDate}
              onChange={handleRangeChange}
              shouldCloseOnSelect={false}
              dateFormat="dd MMM yyyy"
              isClearable
              customInput={
                <DateRangeInput placeholder="Selectează intervalul dorit" />
              }
            />
            <Box
              sx={{
                display: "flex",
                gap: 1,
                mt: 1,
                flexWrap: "nowrap",
              }}
            >
              {quickRangeOptions.map((option) => (
                <Chip
                  key={option.key}
                  label={option.label}
                  onClick={() => handleQuickRangeSelect(option.key)}
                  color={
                    selectedQuickRange === option.key ? "primary" : "default"
                  }
                  variant={
                    selectedQuickRange === option.key ? "filled" : "outlined"
                  }
                  sx={{ flex: 1 }}
                />
              ))}
            </Box>
          </Box>
          <Button variant="text" onClick={handleClearFilters}>
            Resetează filtrele
          </Button>
        </Stack>
      </Paper>

      <Grid container spacing={3} mb={3}>
        <Grid item xs={12} md={4}>
          <Card
            sx={{
              borderRadius: 4,
              p: 2,
              background: "linear-gradient(135deg, #f7971e, #ffd200)",
              color: "common.white",
              boxShadow: "0px 20px 45px rgba(247,151,30,0.35)",
            }}
          >
            <CardContent sx={{ display: "flex", alignItems: "center", p: 0 }}>
              <Box
                sx={{
                  width: 48,
                  height: 48,
                  borderRadius: 2.5,
                  backgroundColor: "rgba(255,255,255,0.25)",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  mr: 2,
                }}
              >
                <Inventory2Icon />
              </Box>
              <Box>
                <Typography variant="body2" sx={{ opacity: 0.85 }}>
                  Total produse restituite
                </Typography>
                <Typography variant="h4" fontWeight={600}>
                  {summaryTotals.count}
                </Typography>
              </Box>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} md={4}>
          <Card
            sx={{
              borderRadius: 4,
              p: 2,
              background: "linear-gradient(135deg, #1cb5e0, #000851)",
              color: "common.white",
              boxShadow: "0px 20px 45px rgba(28,181,224,0.35)",
            }}
          >
            <CardContent sx={{ display: "flex", alignItems: "center", p: 0 }}>
              <Box
                sx={{
                  width: 48,
                  height: 48,
                  borderRadius: 2.5,
                  backgroundColor: "rgba(255,255,255,0.25)",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  mr: 2,
                }}
              >
                <PaidIcon />
              </Box>
              <Box>
                <Typography variant="body2" sx={{ opacity: 0.85 }}>
                  Suma de intrare
                </Typography>
                <Typography variant="h4" fontWeight={600}>
                  {formatMoney(summaryTotals.totalIntrare)}
                </Typography>
              </Box>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} md={4}>
          <Card
            sx={{
              borderRadius: 4,
              p: 2,
              background: "linear-gradient(135deg, #ff416c, #ff4b2b)",
              color: "common.white",
              boxShadow: "0px 20px 45px rgba(255,65,108,0.35)",
            }}
          >
            <CardContent sx={{ display: "flex", alignItems: "center", p: 0 }}>
              <Box
                sx={{
                  width: 48,
                  height: 48,
                  borderRadius: 2.5,
                  backgroundColor: "rgba(255,255,255,0.25)",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  mr: 2,
                }}
              >
                <TrendingDownIcon />
              </Box>
              <Box>
                <Typography variant="body2" sx={{ opacity: 0.85 }}>
                  Profit pierdut
                </Typography>
                <Typography variant="h4" fontWeight={600}>
                  {formatMoney(summaryTotals.profitPierdut)}
                </Typography>
                <Typography variant="caption" sx={{ opacity: 0.8 }}>
                  Calcul: Intrare - Vânzare
                </Typography>
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      <TableContainer
        component={Paper}
        sx={{
          borderRadius: 4,
          boxShadow: "0px 20px 60px rgba(15, 23, 42, 0.1)",
          overflow: "hidden",
        }}
      >
        <Table>
          <TableHead>
            <TableRow sx={{ backgroundColor: "grey.100" }}>
              <TableCell>REC</TableCell>
              <TableCell>Nr. Jurnal</TableCell>
              <TableCell>Tip Contract</TableCell>
              <TableCell sortDirection={sortKey === "sku" ? sortDir : false}>
                <TableSortLabel
                  active={sortKey === "sku"}
                  direction={sortKey === "sku" ? sortDir : "asc"}
                  onClick={() => handleSort("sku")}
                >
                  SKU
                </TableSortLabel>
              </TableCell>
              <TableCell>Nume și Prenume</TableCell>
              <TableCell>Summary</TableCell>
              <TableCell>Preț Intrare</TableCell>
              <TableCell>Preț Vânzare</TableCell>
              <TableCell
                sortDirection={sortKey === "dataRetur" ? sortDir : false}
              >
                <TableSortLabel
                  active={sortKey === "dataRetur"}
                  direction={sortKey === "dataRetur" ? sortDir : "asc"}
                  onClick={() => handleSort("dataRetur")}
                >
                  Data Retur
                </TableSortLabel>
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {sortedData.map((row, index) => (
              <TableRow
                key={index}
                sx={{
                  "&:nth-of-type(even)": { backgroundColor: "grey.50" },
                  "&:hover": { backgroundColor: "rgba(33,150,243,0.08)" },
                  transition: "background-color 0.3s ease",
                }}
              >
                <TableCell>
                  <Checkbox
                    checked={row.rec === 1 || row.rec === "1"}
                    onChange={() =>
                      handleRecChange(
                        row.issue_id,
                        !(row.rec === 1 || row.rec === "1"),
                      )
                    }
                  />
                </TableCell>
                <TableCell>{row.nrJurnalSpecial || "—"}</TableCell>
                <TableCell>{row.tipContract || "—"}</TableCell>
                <TableCell>{row.sku || "—"}</TableCell>
                <TableCell>{row.numeSiPrenume || "—"}</TableCell>
                <TableCell>{row.summary || "—"}</TableCell>
                <TableCell>{formatMoney(row.pretIntrareRON)}</TableCell>
                <TableCell>{formatMoney(row.pretVanzareRON)}</TableCell>
                <TableCell>{formatDisplayDate(row)}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      <Backdrop
        open={isExportingZip}
        sx={{
          color: "#fff",
          zIndex: (theme) => theme.zIndex.drawer + 1,
          backdropFilter: "blur(3px)",
          backgroundColor: "rgba(15, 23, 42, 0.55)",
        }}
      >
        <Stack spacing={2} alignItems="center">
          <CircularProgress color="inherit" />
          <Typography variant="h6">Pregătim arhiva Restituit...</Typography>
          <Typography variant="body2" sx={{ opacity: 0.85 }}>
            Exportul poate dura câteva momente. Te rugăm să aștepți.
          </Typography>
        </Stack>
      </Backdrop>
    </>
  );
}

export default RestituitTab;
